import React, { useMemo, useState } from 'react';
import { Page } from '/imports/ui/Page';
import { PageBody } from '/imports/ui/PageBody';
import { PageHeading } from '/imports/ui/PageHeading';
import { Panel } from '/imports/ui/Panel';
import { Stat, StatRow } from '/imports/ui/Stat';
import { ChipGroup } from '/imports/ui/ChipGroup';
import { LoadingFlex } from '/imports/ui/LoadingFlex';
import { NotPermittedPage } from '/imports/ui/NotPermittedPage';
import { Icon } from '/imports/ui/Icon';
import { useMethod, useUser } from '/imports/hooks';
import { getAdAttribution } from '/imports/api/analytics';

const WINDOWS = [
  { id: 1, label: '24h' },
  { id: 7, label: '7d' },
  { id: 30, label: '30d' },
  { id: 90, label: '90d' },
];

const int = (n: number) => n.toLocaleString();
const pct = (n: number) => `${(n * 100).toFixed(1)}%`;

export const AdAttributionPage = () => {
  const user = useUser();
  const [days, setDays] = useState(30);

  const arg = useMemo(
    () => ({ from: new Date(Date.now() - days * 86400_000), limit: 200 }),
    [days],
  );
  const [data, loading] = useMethod(getAdAttribution, arg, [days]);

  // `user &&`: while the user doc is still loading, render nothing rather
  // than flashing NotPermitted at a superuser.
  if (user && !user.isSuperUser) return <NotPermittedPage />;

  const rows = data?.rows ?? [];
  const totals = data?.totals;
  const stale = data?.staleLeads ?? 0;
  const totalLeads = data?.totalLeads ?? 0;

  return (
    <Page className="page ad-attribution-page" breadcrumbs={[{ path: '/ad-attribution', label: 'Ad Attribution' }]}>
      <PageHeading
        left={<h1><Icon icon="target" />Ad Attribution</h1>}
        right={<ChipGroup ariaLabel="Window" chips={WINDOWS} value={days} onChange={(id) => setDays(id as number)} />}
      />
      <PageBody>
        <StatRow>
          <Stat label="Leads" value={int(totals?.leads ?? 0)} hint="Trades opened while an ad was live" />
          <Stat label="Conversions" value={int(totals?.conversions ?? 0)} hint="Leads that completed a trade" />
          <Stat label="Conversion rate" value={totals ? pct(totals.rate) : '·'} />
          <Stat label="Cards with leads" value={int(totals?.cards ?? 0)} hint="Distinct cards visible in at least one lead's ad" />
        </StatRow>

        {/*
          Stated on the page, not just in the code: this metric is correlational.
          Someone reading a ranking will otherwise assume the top card CAUSED the
          trades, and the first A/B test built on that assumption will mislead.
        */}
        <Panel title="How to read this">
          <div className="text-caption text-fg-2">
            A <strong>lead</strong> is a trade invitation that arrived while the card was visible in the
            classified. We cannot tell whether the customer actually read the ad, so a repeat customer looks
            identical to someone who clicked it. Read these as <em>trades opened while X was advertised</em>,
            not <em>trades caused by advertising X</em>.
            {stale > 0 && (
              <div className="mt-2 text-warn">
                {int(stale)} of {int(totalLeads)} leads ({pct(totalLeads ? stale / totalLeads : 0)}) were
                recorded against an ad older than one rotation, so those customers likely saw a different
                classified than the one credited here.
              </div>
            )}
          </div>
        </Panel>

        <Panel title={`Cards by conversions (${rows.length})`}>
          {loading && !rows.length ? <LoadingFlex /> : (
            <table className="w-full text-ui">
              <thead>
                <tr className="text-left text-label text-fg-3">
                  <th className="py-2">CARD</th>
                  <th>SET</th>
                  <th className="text-right">LEADS</th>
                  <th className="text-right">CONVERSIONS</th>
                  <th className="text-right">RATE</th>
                  <th className="text-right">DECLINED</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.cardId} className="border-t border-soft">
                    <td className="py-1.5">{r.card}</td>
                    <td className="font-mono text-fg-3">{r.set}</td>
                    <td className="text-right font-mono tabular-nums">{int(r.leads)}</td>
                    <td className="text-right font-mono tabular-nums">{int(r.conversions)}</td>
                    <td className="text-right font-mono tabular-nums">{pct(r.rate)}</td>
                    {/* Leads the bot turned away: a high number here means the
                        ad worked and the fleet could not serve the demand. */}
                    <td className="text-right font-mono tabular-nums text-fg-3">{int(r.declined)}</td>
                  </tr>
                ))}
                {!rows.length && !loading && (
                  <tr><td colSpan={6} className="py-4 text-caption text-fg-3">
                    No attribution yet. Rows appear once bots post classifieds and customers open trades.
                  </td></tr>
                )}
              </tbody>
            </table>
          )}
        </Panel>
      </PageBody>
    </Page>
  );
};
